import { buildApiUrl } from '../config/api';

export interface InventoryItem {
  id: string;
  rfid: string;
  name: string;
  palletNumber: string | null;
  last_updated: string;
}

export interface APIResponse {
  success: boolean;
  data: {
    items: InventoryItem[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export interface ItemResponse {
  success: boolean;
  data: InventoryItem;
}

export interface OperationResponse {
  success: boolean;
  message: string;
  count?: number;
}

export interface SearchResponse {
  success: boolean;
  data: any;
}

export interface BatchDetailResponse {
  success: boolean;
  data: {
    batchNumber: string;
    palletNumbers: string[];
    totalCount: number;
  };
}

const BASE_URL = buildApiUrl('/api/v1');

// 獲取請求 headers
const getHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

// 查詢庫存項目
export const fetchInventoryItems = async (status?: string, page: number = 1, pageSize: number = 20, search?: string): Promise<APIResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      status: status || 'all',
    });
    
    if (search) {
      params.append('search', search);
    }
    
    const response = await fetch(`${BASE_URL}/inventory/items?${params}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error?.message || `Failed to fetch inventory items. Status: ${response.status}`
      );
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 查詢單一庫存項目
export const getInventoryItem = async (rfid: string): Promise<InventoryItem> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/items/${rfid}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to get inventory item');
    }
    
    const result: ItemResponse = await response.json();
    return result.data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 更新庫存項目 (by RFID)
export const updateInventoryByRFID = async (rfid: string, itemData: Partial<InventoryItem> & { id?: string }): Promise<InventoryItem> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/items/${rfid}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        name: itemData.name,
        palletNumber: itemData.palletNumber,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to update inventory item');
    }
    
    const result: ItemResponse = await response.json();
    return result.data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 入庫功能
export const performInventoryInbound = async (rfid: string, name: string): Promise<void> => {
  if (!rfid || !name) {
    throw new Error('RFID和批號都是必填項目');
  }
  
  try {
    const response = await fetch(`${BASE_URL}/inventory/operations/inbound`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ rfid, name }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `入庫失敗 (HTTP ${response.status})`);
    }
    
    const result: OperationResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || '入庫操作失敗');
    }
    
    // 後端會處理 Firestore 同步通知
  } catch (error) {
    console.error('Inbound Error:', error);
    throw error;
  }
};

// 出庫功能
export const performInventoryOutbound = async (rfid: string): Promise<void> => {
  if (!rfid) {
    throw new Error('RFID is required');
  }
  
  try {
    const response = await fetch(`${BASE_URL}/inventory/operations/outbound`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ rfids: [rfid] }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `出庫失敗 (HTTP ${response.status})`);
    }
    
    const result: OperationResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || '出庫操作失敗');
    }
  } catch (error) {
    console.error('Outbound Error:', error);
    throw error;
  }
};

// 批次入庫
export const performBatchInbound = async (items: Array<{ rfid: string; name: string }>): Promise<OperationResponse> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/operations/inbound`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ items }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to perform batch inbound');
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 批次出庫
export const performBatchOutbound = async (rfids: string[]): Promise<OperationResponse> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/operations/outbound`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ rfids }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to perform batch outbound');
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 更新棧板號碼
export const updatePalletNumber = async (rfid: string, palletNumber: string): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/items/batch/pallet`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ rfids: [rfid], palletNumber }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to update pallet number');
    }
    
    // 後端現在會處理 Firestore 同步通知
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 批次更新棧板號碼
export const updateBatchPalletNumbers = async (rfids: string[], palletNumber: string): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/items/batch/pallet`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ rfids, palletNumber }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to update pallet numbers');
    }
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 批號查詢棧板資訊
export const getBatchPallet = async (batchNumber: string): Promise<BatchDetailResponse> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/batches/${encodeURIComponent(batchNumber)}`, {
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to get batch pallet info');
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 搜尋功能
export const searchInventory = async (query: string): Promise<SearchResponse> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ query }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to search inventory');
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 根據 RFID 查詢批號（用於相容舊功能）
export const getItemsNameByRFIDs = async (rfids: string[]): Promise<string[]> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ type: 'names', rfids }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to get items name');
    }
    
    const result: SearchResponse = await response.json();
    return result.data.names || [];
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 根據批號查詢 RFID（用於相容舊功能）
export const getItemsRFIDByBatch = async (batch: string): Promise<string[]> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ type: 'rfids', batch }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to get items RFID');
    }
    
    const result: SearchResponse = await response.json();
    return result.data.rfids || [];
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// 刪除庫存項目（根據批號）
export const deleteInventoryByBatch = async (batchNumber: string): Promise<OperationResponse> => {
  try {
    const response = await fetch(`${BASE_URL}/inventory/items/batch`, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify({ batchNumber }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to delete inventory items');
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};
